import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Calendar, MapPin } from 'lucide-react';

const Experience = () => {
  const experiences = [
    {
      title: 'Node.js Backend Developer Intern',
      company: 'York IE (APAC Pvt. Ltd.)',
      location: 'Ahmedabad, Gujarat',
      period: 'Jan 2026 - Jul 2026',
      description: [
        'Built an asynchronous multi-worker AI document processing pipeline using AWS Textract and Bedrock (Claude Sonnet)',
        'Contributed to MedicalEase, a production AWS-hosted medico-legal SaaS with five role-based portals'
      ]
    },
    {
      title: 'Network Developer (Intern)',
      company: 'Megaminds IT Services',
      location: 'Remote',
      period: 'July 2024 - Jan 2025',
      description: [
        'Developed digital signature verification for Mobile Ad-Hoc Networks (MANETs)',
        'Simulated networks in NS2; analyzed pcap traffic in Wireshark for threat detection'
      ]
    },
    {
      title: 'Cybersecurity (Intern)',
      company: 'Haryana Police (GPCSSI\'24)',
      location: 'Haryana, India',
      period: 'June 2024 - July 2024',
      description: [
        'Assisted in detection and tracking of illicit activities through Dark Web monitoring',
        'Collaborated with law enforcement to assess cybercrime trends'
      ]
    },
    {
      title: 'Head of Cybersecurity (Intern)',
      company: 'CLUMOSS',
      location: 'Remote',
      period: 'Aug 2023 - Oct 2023',
      description: [
        'Led development and implementation of security policies across the organization',
        'Trained and mentored 20+ cybersecurity interns through security exercises'
      ]
    },
    {
      title: 'Blue Teaming (Intern)',
      company: 'Virtual Cyber Labs',
      location: 'Remote',
      period: 'Nov 2022 - Jan 2023',
      description: [
        'Performed blue teaming operations focusing on network defense and monitoring',
        'Monitored network traffic to identify potential threats and malicious activities'
      ]
    }
  ];

  return (
    <section id="experience" className="py-24 bg-gray-50 dark:bg-[#1c1c1e] transition-colors duration-300">
      <div className="container mx-auto px-6 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-4 font-inter">
            Experience
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 font-inter">My professional journey and internships.</p>
        </motion.div>

        <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 dark:before:via-gray-800 before:to-transparent">
          {experiences.map((exp, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group"
            >
              {/* Icon / Dot */}
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 dark:border-gray-800 bg-white dark:bg-black text-blue-600 dark:text-blue-400 shadow-sm z-10 shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                <Briefcase size={18} />
              </div>

              {/* Card */}
              <div className="w-[calc(100%-4rem)] md:w-[45%] p-6 apple-card">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{exp.title}</h3>
                  <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    <Calendar size={12} /> {exp.period}
                  </span>
                </div>
                
                <div className="flex flex-col gap-1 mb-4">
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">{exp.company}</span>
                  <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                    <MapPin size={14} /> {exp.location}
                  </span>
                </div>

                <ul className="space-y-2">
                  {exp.description.map((item, i) => (
                    <li key={i} className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex gap-2">
                      <span className="text-blue-500 mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Volunteer Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mt-24 max-w-2xl mx-auto p-8 rounded-3xl border border-dashed border-gray-200 dark:border-gray-800 text-center"
        >
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Volunteer Experience</h3>
          <p className="text-blue-600 dark:text-blue-400 font-semibold mb-2">Technical Coordinator</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Department of Information Technology | 2023 - 2024</p>
          <p className="text-gray-600 dark:text-gray-400 text-sm italic">Conducted & managed technical & non-technical events for the department.</p>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
